import { dateOffset, weekStart, type CalendarEvent } from './calendar_sync';
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Kyiv' });
const escape = (s: string) =>
    s.replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!
    );
const dateLabel = (date: string, options: Intl.DateTimeFormatOptions) =>
    new Date(`${date}T12:00:00Z`).toLocaleDateString('uk-UA', {
        ...options,
        timeZone: 'Europe/Kyiv',
    });
let month = today().slice(0, 7),
    selected = today(),
    events: CalendarEvent[] = [],
    request = 0;
let activeView: 'slots' | 'calendar' = 'slots';
function eventCount(n: number) {
    const unit = n % 10,
        last = n % 100;
    return `${n} ${unit === 1 && last !== 11 ? 'подія' : unit >= 2 && unit <= 4 && (last < 12 || last > 14) ? 'події' : 'подій'}`;
}
function gridStart() {
    return weekStart(`${month}-01`);
}
async function api(url: string, options?: RequestInit) {
    const res = await fetch(url, options),
        data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Не вдалося завантажити події');
    return data;
}
function message(text: string) {
    const el = document.getElementById('event-calendar-message');
    if (el) el.textContent = text;
}
async function load() {
    const token = ++request;
    try {
        const data = await api(
            `api/calendar.php?from=${gridStart()}&to=${dateOffset(gridStart(), 41)}`
        );
        if (token !== request) return;
        events = data.data;
        render();
    } catch (error) {
        message((error as Error).message);
    }
}
function selectView(view: 'slots' | 'calendar') {
    activeView = view;
    document.getElementById('slots-schedule-panel')!.hidden = view !== 'slots';
    document.getElementById('event-calendar')!.hidden = view !== 'calendar';
    document.querySelectorAll<HTMLElement>('[data-slots-view]').forEach((button) => {
        const active = button.dataset.slotsView === view;
        button.classList.toggle('active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
    });
    if (view === 'calendar') load();
    else requestAnimationFrame(() => (window as any).syncTimelineHeightWithSidebar?.());
}
function render() {
    const grid = document.getElementById('event-month-grid');
    if (!grid) return;
    const monthName = dateLabel(`${month}-01`, { month: 'long' });
    document.getElementById('event-month-label')!.textContent =
        `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${month.slice(0, 4)}`;
    document.getElementById('event-selected-date')!.textContent = dateLabel(selected, {
        day: 'numeric',
        month: 'long',
    });
    document.getElementById('event-selected-weekday')!.textContent = dateLabel(selected, {
        weekday: 'long',
    });
    (document.getElementById('event-jump') as HTMLInputElement).value = selected;
    const dayEvents = events.filter((event) => event.event_date === selected);
    const monthCount = events.filter((event) => event.event_date.startsWith(month)).length;
    document.getElementById('event-month-count')!.textContent = eventCount(monthCount);
    const start = gridStart();
    grid.innerHTML = Array.from({ length: 42 }, (_, index) => {
        const date = dateOffset(start, index),
            dayEvents = events.filter((event) => event.event_date === date);
        return `<button type="button" class="event-day ${date === selected ? 'is-selected' : ''} ${date === today() ? 'is-today' : ''} ${!date.startsWith(month) ? 'is-outside' : ''}" data-event-date="${date}" aria-label="${escape(dateLabel(date, { day: 'numeric', month: 'long' }))}, подій: ${dayEvents.length}" aria-pressed="${date === selected}"><span class="event-day-number">${Number(date.slice(8))}</span><span class="event-day-items">${dayEvents
            .slice(0, 2)
            .map(
                (event) =>
                    `<span class="event-day-chip" title="${escape(event.title)}"><time>${event.start_time.slice(0, 5)}</time><span>${escape(event.title)}</span></span>`
            )
            .join(
                ''
            )}${dayEvents.length > 2 ? `<span class="event-day-more">Ще ${dayEvents.length - 2}</span>` : ''}</span>${dayEvents.length ? `<span class="event-day-dot"></span>` : ''}</button>`;
    }).join('');
    document.getElementById('event-day-count')!.textContent = eventCount(dayEvents.length);
    document.getElementById('event-day-list')!.innerHTML = dayEvents.length
        ? dayEvents
              .map(
                  (event) =>
                      `<article class="event-agenda-item"><div class="event-agenda-time"><i class="ri-time-line"></i>${event.start_time.slice(0, 5)} — ${event.end_time.slice(0, 5)}</div><strong>${escape(event.title)}</strong><span class="event-reserved"><i class="ri-lock-line"></i> Час зарезервовано у слотах</span><div class="event-agenda-actions"><button type="button" data-edit-event="${event.id}" class="event-button"><i class="ri-edit-line"></i> Змінити</button><button type="button" data-delete-event="${event.id}" class="event-button event-delete" aria-label="Видалити ${escape(event.title)}"><i class="ri-delete-bin-line"></i></button></div></article>`
              )
              .join('')
        : `<div class="event-empty"><span class="event-empty-icon"><i class="ri-calendar-check-line"></i></span><strong>День без подій</strong><p>Додайте захід або зустріч.<br>Цей час буде враховано у слотах.</p><button type="button" class="event-button" data-new-event><i class="ri-add-line"></i> Додати подію</button></div>`;
}
function field(name: string) {
    return document.querySelector<HTMLInputElement>(`#event-form [name="${name}"]`)!;
}
function editor() {
    return document.getElementById('event-editor') as HTMLDialogElement;
}
function openEditor(event?: CalendarEvent) {
    (document.getElementById('event-form') as HTMLFormElement).reset();
    field('id').value = event ? String(event.id) : '';
    field('title').value = event?.title || '';
    field('event_date').value = event?.event_date || selected;
    field('start_time').value = event?.start_time.slice(0, 5) || '18:00';
    field('end_time').value = event?.end_time.slice(0, 5) || '20:00';
    document.getElementById('event-editor-title')!.textContent = event
        ? 'Редагувати подію'
        : 'Нова подія';
    document.getElementById('event-form-error')!.textContent = '';
    editor().showModal();
    field('title').focus();
}
async function changed() {
    await load();
    window.dispatchEvent(new CustomEvent('calendar-events-changed'));
}
document.addEventListener('DOMContentLoaded', () => {
    const schedule = document.querySelector<HTMLElement>('#slots .slots-workspace');
    if (!schedule) return;
    schedule.id = 'slots-schedule-panel';
    schedule.setAttribute('role', 'tabpanel');
    schedule.setAttribute('aria-labelledby', 'slots-view-schedule');
    schedule.insertAdjacentHTML(
        'beforebegin',
        `<nav class="slots-view-tabs" role="tablist" aria-label="Планування"><button type="button" id="slots-view-schedule" class="sub-tab-button active" data-slots-view="slots" role="tab" aria-selected="true" aria-controls="slots-schedule-panel"><i class="ri-timer-line"></i> Слоти</button><button type="button" id="slots-view-calendar" class="sub-tab-button" data-slots-view="calendar" role="tab" aria-selected="false" aria-controls="event-calendar" tabindex="-1"><i class="ri-calendar-event-line"></i> Календар</button></nav><section id="event-calendar" class="event-calendar" role="tabpanel" aria-labelledby="slots-view-calendar" hidden><div class="event-calendar-toolbar"><div class="event-month-heading"><h2 id="event-month-label"></h2><span id="event-month-count" class="event-count"></span></div><div class="event-calendar-controls"><div class="event-nav-group"><button type="button" class="event-button event-icon-button" data-event-nav="-1" aria-label="Попередній місяць"><i class="ri-arrow-left-s-line"></i></button><button type="button" class="event-button" id="event-today">Сьогодні</button><button type="button" class="event-button event-icon-button" data-event-nav="1" aria-label="Наступний місяць"><i class="ri-arrow-right-s-line"></i></button></div><label class="event-jump-label"><i class="ri-calendar-line"></i><input type="date" id="event-jump" value="${selected}" aria-label="Перейти до дати"></label><button type="button" class="event-button event-button-primary" data-new-event><i class="ri-add-line"></i> Нова подія</button></div></div><div class="event-calendar-layout"><div class="event-month-card"><div class="event-weekday-row">${['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'].map((day) => `<span>${day}</span>`).join('')}</div><div id="event-month-grid" class="event-month-grid"></div><div class="event-calendar-legend"><span><i class="event-legend-dot"></i> Подія календаря</span><span><i class="ri-link"></i> Синхронізовано зі слотами</span></div></div><aside class="event-agenda"><header class="event-agenda-header"><span id="event-selected-weekday"></span><h3 id="event-selected-date"></h3><span id="event-day-count" class="event-count"></span></header><div id="event-day-list"></div><button type="button" id="event-show-week" class="event-button event-open-slots"><i class="ri-timer-line"></i> Відкрити день у слотах<i class="ri-arrow-right-line"></i></button></aside></div><p id="event-calendar-message" role="status"></p><dialog id="event-editor" class="event-editor-dialog" aria-labelledby="event-editor-title"><form id="event-form"><header class="event-editor-header"><h3 id="event-editor-title">Нова подія</h3><button type="button" class="event-button event-icon-button" data-close-event aria-label="Закрити"><i class="ri-close-line"></i></button></header><div class="event-editor-fields"><input type="hidden" name="id"><label>Назва події<input name="title" required maxlength="200" placeholder="Наприклад, зустріч або захід"></label><label>Дата<input type="date" name="event_date" required></label><div class="event-editor-times"><label>Початок<input type="time" name="start_time" required></label><label>Завершення<input type="time" name="end_time" required></label></div><p class="event-editor-hint"><i class="ri-link"></i> Час події резервується у слотах. Доставка з цього проміжку переходить у перепланування.</p><p id="event-form-error" role="alert"></p></div><footer class="event-editor-footer"><button type="button" class="event-button" data-close-event>Скасувати</button><button type="submit" class="event-button event-button-primary">Зберегти подію</button></footer></form></dialog></section>`
    );
    document.querySelector('.slots-view-tabs')!.addEventListener('click', (e) => {
        const button = (e.target as HTMLElement).closest<HTMLElement>('[data-slots-view]');
        if (button) selectView(button.dataset.slotsView as 'slots' | 'calendar');
    });
    document.querySelector('.slots-view-tabs')!.addEventListener(
        'keydown',
        (e) => {
            const key = (e as KeyboardEvent).key;
            if (key === 'Enter' || key === ' ') {
                const button = (e.target as HTMLElement).closest<HTMLElement>('[data-slots-view]');
                if (button) {
                    e.preventDefault();
                    e.stopPropagation();
                    selectView(button.dataset.slotsView as 'slots' | 'calendar');
                }
                return;
            }
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;
            e.preventDefault();
            const view =
                key === 'Home'
                    ? 'slots'
                    : key === 'End'
                      ? 'calendar'
                      : activeView === 'slots'
                        ? 'calendar'
                        : 'slots';
            selectView(view);
            document.querySelector<HTMLElement>(`[data-slots-view="${view}"]`)?.focus();
        },
        true
    );
    const root = document.getElementById('event-calendar')!;
    root.addEventListener('click', async (e) => {
        const button = (e.target as HTMLElement).closest<HTMLElement>('button');
        if (!button) return;
        if (button.dataset.slotsView) return;
        if ('newEvent' in button.dataset) openEditor();
        if ('closeEvent' in button.dataset) editor().close();
        if (button.dataset.eventNav) {
            const date = new Date(`${month}-01T12:00:00Z`);
            date.setUTCMonth(date.getUTCMonth() + Number(button.dataset.eventNav));
            month = date.toISOString().slice(0, 7);
            selected = `${month}-01`;
            message('');
            await load();
        }
        if (button.dataset.eventDate) {
            selected = button.dataset.eventDate;
            const nextMonth = selected.slice(0, 7);
            if (nextMonth !== month) {
                month = nextMonth;
                await load();
            } else render();
        }
        if (button.id === 'event-today') {
            selected = today();
            month = selected.slice(0, 7);
            message('');
            await load();
        }
        if (button.id === 'event-show-week') {
            selectView('slots');
            window.dispatchEvent(
                new CustomEvent('calendar-view-week', {
                    detail: { monday: weekStart(selected), date: selected },
                })
            );
        }
        if (button.dataset.editEvent) {
            const event = events.find(
                (item) => Number(item.id) === Number(button.dataset.editEvent)
            );
            if (event) openEditor(event);
        }
        if (button.dataset.deleteEvent) {
            try {
                await api('api/calendar.php', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: Number(button.dataset.deleteEvent) }),
                });
                await changed();
                message('Подію видалено. Розклад слотів оновлено.');
            } catch (error) {
                message((error as Error).message);
            }
        }
    });
    editor().addEventListener('click', (e) => {
        if (e.target === editor()) {
            const rect = editor().getBoundingClientRect();
            const mouse = e as MouseEvent;
            if (
                mouse.clientX < rect.left ||
                mouse.clientX > rect.right ||
                mouse.clientY < rect.top ||
                mouse.clientY > rect.bottom
            )
                editor().close();
        }
    });
    document.getElementById('event-jump')!.addEventListener('change', async (e) => {
        const value = (e.target as HTMLInputElement).value;
        if (!value) return;
        selected = value;
        month = value.slice(0, 7);
        message('');
        await load();
    });
    document.getElementById('event-form')!.addEventListener('submit', async (e) => {
        e.preventDefault();
        const form = e.target as HTMLFormElement,
            button = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
        if (field('start_time').value >= field('end_time').value) {
            document.getElementById('event-form-error')!.textContent =
                'Завершення має бути після початку.';
            return;
        }
        button.disabled = true;
        try {
            const data = Object.fromEntries(new FormData(form));
            await api('api/calendar.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            selected = String(data.event_date);
            month = selected.slice(0, 7);
            editor().close();
            await changed();
            message('Подію збережено. Слоти оновлено.');
        } catch (error) {
            document.getElementById('event-form-error')!.textContent = (error as Error).message;
        } finally {
            button.disabled = false;
        }
    });
    window.addEventListener('focus', () => {
        if (activeView === 'calendar' && !editor().open) load();
    });
    render();
});
